/**
 * One external ("off-platform") booking in a host's reservation list.
 *
 * These are not Sharetribe transactions, so they can't reuse the transaction row:
 * there is no `/sale/:id` page to link to, no counterparty user entity and no
 * `lineItems`. The badge is there so a host can tell at a glance which reservations
 * came in through "Proteger reserva externa".
 *
 * The figure shown is the host's **estimated net payout**, not the guest's price: the
 * reduced commission and the insurance come out of it. It is the quote taken when the
 * offer was created — once the bañista books, the transaction's line items are
 * authoritative. See docs/external-bookings.md.
 */

import React, { useState } from 'react';
import { func, object, shape, string } from 'prop-types';

import MaterialSymbol from '../MaterialSymbol/MaterialSymbol';
import { copyTextToClipboard } from '../../util/clipboard';
import {
  EXTERNAL_BOOKING_STATUS,
  externalBookingPaymentLink,
  externalBookingStatusLabel,
  formatGuestRangeLabel,
  formatSubunits,
} from '../../util/externalBookings';
import css from './ExternalBookingRow.module.css';

const dateOptions = { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' };
const timeOptions = { hour: '2-digit', minute: '2-digit' };

const ExternalBookingRow = props => {
  const { booking, intl, onCancel } = props;
  const [copied, setCopied] = useState(false);
  // Resolved server-side and carried on the row, so it never depends on some other
  // request having populated a listings cache first.
  const listingTitle = booking.listingTitle;
  const start = new Date(booking.bookingStart);
  const end = new Date(booking.bookingEnd);
  const isPending = booking.status === EXTERNAL_BOOKING_STATUS.PENDING;

  // The link the bañista needs. Offered here because the guest's email is optional —
  // and even when given, the email can silently fail to arrive (wrong address, spam,
  // an unverified sender) — so the host must always have a way to get the offer out.
  const paymentLink = externalBookingPaymentLink(booking);

  const handleCopy = () => {
    copyTextToClipboard(paymentLink).then(ok => {
      if (ok) {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2500);
      }
    });
  };

  return (
    <div className={css.root}>
      <div className={css.main}>
        <div className={css.badge}>
          <MaterialSymbol name="shield" size={16} />
          <span>Reserva externa protegida</span>
        </div>
        <div className={css.guest}>{booking.guestName}</div>
        {listingTitle ? <div className={css.listing}>{listingTitle}</div> : null}
        <div className={css.meta}>{formatGuestRangeLabel(booking)}</div>
      </div>

      <div className={css.when}>
        <span className={css.whenLabel}>Inicio de la reserva</span>
        <span className={css.whenTime}>{intl.formatTime(start, timeOptions)}</span>
        <span className={css.whenDate}>{intl.formatDate(start, dateOptions)}</span>
        <span className={css.whenRange}>hasta {intl.formatTime(end, timeOptions)}</span>
      </div>

      <div className={css.side}>
        <span className={isPending ? `${css.status} ${css.statusPending}` : css.status}>
          {externalBookingStatusLabel(booking.status)}
        </span>
        <span className={css.payout}>
          {formatSubunits(intl, booking.hostPayoutSubunits, booking.currency)}
        </span>
        <span className={css.payoutLabel}>liquidación estimada</span>
        {isPending && paymentLink ? (
          <button type="button" className={css.copyButton} onClick={handleCopy}>
            <MaterialSymbol name={copied ? 'check' : 'content_copy'} size={16} />
            {copied ? 'Enlace copiado' : 'Copiar enlace'}
          </button>
        ) : null}
        {/* Cancelling is only offered while pending: once the bañista has booked it is
            an ordinary transaction and cancelling means refunding. */}
        {isPending && onCancel ? (
          <button type="button" className={css.cancelButton} onClick={() => onCancel(booking)}>
            Cancelar
          </button>
        ) : null}
      </div>
    </div>
  );
};

ExternalBookingRow.propTypes = {
  booking: shape({
    id: string.isRequired,
    status: string.isRequired,
    guestName: string.isRequired,
  }).isRequired,
  intl: object.isRequired,
  onCancel: func,
};

export default ExternalBookingRow;
